import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import YooLogo from "../components/YooLogo";
import {
  authenticateWithGoogle,
  authenticateWithEmail,
  createStripeCheckoutSession,
  verifyPaymentSuccess,
  getUserCredits,
  getCurrentUser,
} from "../api/authPaymentAPI";

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [user, setUser] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    // Check if we have a token
    const token = localStorage.getItem("token");

    if (token) {
      fetchUserData();
    }

    // Initialize Google Sign-In
    const loadGoogleSignIn = () => {
      window.google?.accounts.id.initialize({
        client_id:
          "440646708875-us4fq5qur5s268ra38vr2bnpmcg975r1.apps.googleusercontent.com",
        callback: handleGoogleSignIn,
      });

      window.google?.accounts.id.renderButton(
        document.getElementById("googleSignInButton"),
        { theme: "outline", size: "large", width: 280 }
      );
    };

    // Load Google Sign-In API
    if (!window.google?.accounts) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = loadGoogleSignIn;
      document.body.appendChild(script);
    } else {
      loadGoogleSignIn();
    }
  }, []);

  // Fetch user data from server using token
  const fetchUserData = async () => {
    try {
      setIsProcessing(true);
      const userData = await getCurrentUser();
      setUser(userData);
      setIsSignedIn(true);

      // If we're coming back from a successful payment (check URL query params)
      const params = new URLSearchParams(location.search);
      const sessionId = params.get("session_id");

      if (sessionId && !paymentSuccess) {
        verifyAndUpdateCredits(sessionId, userData.id);
      }
    } catch (err) {
      console.error("Failed to fetch user data:", err);
      // If token is invalid, clear it
      localStorage.removeItem("token");
    } finally {
      setIsProcessing(false);
    }
  };

  // Verify payment and update user credits
  const verifyAndUpdateCredits = async (sessionId, userId) => {
    try {
      setIsProcessing(true);

      // Clear the URL parameters immediately to prevent duplicate processing on refreshes
      window.history.replaceState({}, document.title, "/subscribe");

      const result = await verifyPaymentSuccess(sessionId);

      if (result.success) {
        // If already processed, don't update the UI again
        if (result.alreadyProcessed) {
          console.log("Payment was already processed previously");
          setPaymentSuccess(true);
          setError({
            type: "success",
            message:
              "Your payment has already been processed. Your account has been credited.",
          });
          return;
        }

        // Fetch updated user data after payment
        await fetchUserData();
        setPaymentSuccess(true);

        // Show success message
        setError({
          type: "success",
          message:
            "Payment successful! Your credits have been added to your account.",
        });
      }
    } catch (err) {
      setError({
        type: "error",
        message: "Failed to verify payment: " + err.message,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Clear error messages after 5 seconds
  useEffect(() => {
    if (error && error.type === "error") {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleGoogleSignIn = async (response) => {
    try {
      setIsProcessing(true);

      // Send the Google credential to our backend for verification
      const userData = await authenticateWithGoogle(response.credential);

      // Set user data from the response
      setUser(userData);
      setIsSignedIn(true);
    } catch (err) {
      setError({
        type: "error",
        message: "Sign-in failed: " + err.message,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();

    try {
      setIsProcessing(true);
      const email = e.target.email.value;
      const password = e.target.password.value;

      // Authenticate with our backend
      const userData = await authenticateWithEmail(email, password);

      // Set user data from the response
      setUser(userData);
      setIsSignedIn(true);
    } catch (err) {
      setError({
        type: "error",
        message: "Sign-in failed: " + err.message,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
  };

  const handlePurchase = async () => {
    if (!selectedPlan) {
      setError({
        type: "error",
        message: "Please select a credit plan",
      });
      return;
    }

    try {
      setIsProcessing(true);

      // Create a checkout session through our backend
      const checkoutData = await createStripeCheckoutSession(
        selectedPlan,
        user.id
      );

      // Redirect to the Stripe checkout page
      window.location.href = checkoutData.url;

      // Note: After successful payment, Stripe will redirect back to our success URL
      // which will have a session_id parameter that we'll verify in useEffect
    } catch (err) {
      setError({
        type: "error",
        message: "Payment processing failed: " + err.message,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Sign out function
  const handleSignOut = () => {
    localStorage.removeItem("token");
    setUser(null);
    setIsSignedIn(false);
  };

  // Credit plans (1 credit = $0.50)
  const creditPlans = [
    { id: 1, credits: 10, price: 5.0, name: "Basic" },
    { id: 2, credits: 25, price: 12.5, name: "Standard" },
    { id: 3, credits: 50, price: 25.0, name: "Premium" },
    { id: 4, credits: 100, price: 50.0, name: "Ultimate" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="py-2 px-8 bg-white flex justify-between items-center border-b border-gray-200">
        <Link to="/" className="text-[#23c55e] text-3xl font-bold">
          <YooLogo size="lg" />
        </Link>

        <div className="flex items-center">
          {isSignedIn && (
            <Link to="/joke" className="text-blue-600 hover:text-blue-800">
              Back to Jokes
            </Link>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex flex-col items-center justify-center p-4">
        <div className="max-w-3xl w-full bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-800">
              {isSignedIn ? "Choose Your Credit Plan" : "Join Yoo! Premium"}
            </h1>
            <p className="mt-2 text-gray-600">
              {isSignedIn
                ? "Select a credit plan to continue enjoying premium jokes"
                : "Sign in to purchase credits and unlock unlimited jokes"}
            </p>
          </div>

          {error && (
            <div
              className={`mx-6 mt-4 p-3 border rounded ${
                error.type === "success"
                  ? "bg-green-100 border-green-400 text-green-700"
                  : "bg-red-100 border-red-400 text-red-700"
              }`}
            >
              {error.message}
            </div>
          )}

          <div className="p-6">
            {!isSignedIn ? (
              <div className="grid md:grid-cols-2 gap-8">
                {/* Google Sign In */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h2 className="text-xl font-semibold mb-4">
                    Sign in with Google
                  </h2>
                  <div
                    id="googleSignInButton"
                    className="flex justify-center"
                  ></div>
                </div>

                {/* Email Sign In */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h2 className="text-xl font-semibold mb-4">
                    Sign in with Email
                  </h2>
                  <form onSubmit={handleEmailSignIn} className="space-y-4">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-gray-700 mb-1"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full p-2 border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-gray-700 mb-1"
                      >
                        Password
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        required
                        className="w-full p-2 border border-gray-300 rounded"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isProcessing}
                      className={`w-full py-2 rounded font-medium ${
                        isProcessing
                          ? "bg-gray-400 cursor-not-allowed text-white"
                          : "bg-[#23c55e] text-white hover:bg-[#1ea04d]"
                      } transition-colors`}
                    >
                      {isProcessing ? "Processing..." : "Sign In"}
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              <div>
                {/* User Information */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">
                    Signed in as{" "}
                    <span className="font-semibold">
                      {user?.email || user?.name}
                    </span>
                  </p>
                  <p className="text-gray-700">
                    Current credits:{" "}
                    <span className="font-semibold">{user?.credits || 0}</span>
                  </p>
                </div>

                {/* Credit Plans */}
                <h2 className="text-xl font-semibold mb-4">
                  Select a Credit Plan
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {creditPlans.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => handlePlanSelection(plan)}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedPlan?.id === plan.id
                          ? "border-[#23c55e] bg-green-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <h3 className="text-lg font-semibold">{plan.name}</h3>
                      <p className="text-2xl font-bold text-[#23c55e] my-2">
                        ${plan.price.toFixed(2)}
                      </p>
                      <p className="text-gray-600">{plan.credits} credits</p>
                      <p className="text-xs text-gray-500 mt-1">
                        (${(plan.price / plan.credits).toFixed(2)} per credit)
                      </p>
                    </div>
                  ))}
                </div>

                {/* Purchase Button */}
                <div className="flex justify-center">
                  <button
                    onClick={handlePurchase}
                    disabled={!selectedPlan || isProcessing}
                    className={`px-8 py-3 rounded-lg font-semibold ${
                      !selectedPlan || isProcessing
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-[#23c55e] text-white hover:bg-[#1ea04d]"
                    } transition-colors`}
                  >
                    {isProcessing
                      ? "Processing..."
                      : `Purchase ${selectedPlan?.credits || 0} Credits`}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              By purchasing credits, you agree to our Terms and Conditions and
              Privacy Policy.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
